import { ParsedLocale } from 'lib/constants/localization'
import { useRouter } from 'next/router'
import { getCountryFlagIcon } from 'lib/features/country'
import { languageDomains } from 'lib/constants/language'
import { languageToCountryCode } from 'lib/formatting/language'
import { Body2 } from 'stories/elements/Typography/Text'
import { returnCountrySwitcherLabelByContentLanguage } from 'lib/constants/localizedCountryLabels'
import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import Arrow from 'assets/icons/menu_arrow.svg'
import { countryDomains } from 'lib/constants/country'

const DropdownContainer = styled.div`
    position: relative;
    width: 100%;
`

const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background-color: ${theme.colors.grayScalePrimaryTransparent};
    color: white;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    justify-content: space-between;
`

const DropdownList = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: 93%;
    left: 0;
    right: 0;
    max-height: 200px;
    overflow-y: auto;
    background-color: ${theme.colors.grayScalePrimaryTransparent};
    border-radius: 0 0 6px 6px;
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    z-index: 10;
`

const DropdownItem = styled.a`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    cursor: pointer;
    text-decoration: none;

    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`

const ArrowIcon = styled(Arrow)<{ isActive: boolean }>`
    transform: rotate(180deg);
    -ms-transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    -webkit-transition: -webkit-transform 0.35s ease-in-out;
    -ms-transition: -ms-transform 0.35s ease-in-out;
    transition: transform 0.35s ease-in-out;
    ${({ isActive }) =>
        isActive
            ? css`
                  transform: rotate(0deg);
                  -ms-transform: rotate(0deg);
                  -webkit-transform: rotate(0deg);
              `
            : ''}
`

const LabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

interface Props {
    parsedLocale: ParsedLocale
}

const CustomLocaleDropdown = ({ parsedLocale }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const router = useRouter()
    const { asPath } = router
    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    if (!parsedLocale) return null

    const countryOptions = Object.values(languageDomains).map((v) => {
        const countryCode = languageToCountryCode(v)
        const domain = Object.entries(countryDomains).find(([_, code]) => code === countryCode)?.[0] || ''
        return {
            value: v,
            label: returnCountrySwitcherLabelByContentLanguage({ contentLanguageCode: v } as ParsedLocale),
            countryCode,
            domain: domain ? `https://${domain}${asPath}` : '',
        }
    })

    const currentCountryCode = languageToCountryCode(parsedLocale.contentLanguageCode)
    const currentCountryName = returnCountrySwitcherLabelByContentLanguage(parsedLocale)

    const toggleDropdown = () => setIsOpen(!isOpen)

    return (
        <DropdownContainer ref={dropdownRef}>
            <DropdownHeader onClick={toggleDropdown}>
                <LabelContainer>
                    {getCountryFlagIcon(currentCountryCode)}
                    <Body2 color="white" fontFamily="var(--font-inter)" fontWeight={400}>
                        {currentCountryName}
                    </Body2>
                </LabelContainer>
                <ArrowIcon isActive={isOpen} />
            </DropdownHeader>
            <DropdownList isOpen={isOpen}>
                {countryOptions.map((option) => (
                    <DropdownItem
                        key={option.value}
                        href={option.domain}
                        onClick={(e) => {
                            if (!option.domain) {
                                e.preventDefault()
                                router.push(asPath, asPath, { locale: option.value })
                            }
                            setIsOpen(false)
                        }}
                    >
                        {getCountryFlagIcon(option.countryCode)}
                        <Body2 color="white" fontFamily="var(--font-inter)" fontWeight={400}>
                            {option.label}
                        </Body2>
                    </DropdownItem>
                ))}
            </DropdownList>
        </DropdownContainer>
    )
}

export default CustomLocaleDropdown
